import React, { useState, useEffect } from 'react';
import { Lightbulb, CheckCircle, Clock, MessageSquare, Send, AlertTriangle } from 'lucide-react';

type Task = {
  id: string;
  title: string;
  description: string;
  careerPath: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  deadline: string;
  status: 'pending' | 'in-progress' | 'submitted' | 'completed';
};

type AITaskAssignmentProps = {
  careerPath: string;
  onChatWithMentor?: () => void;
};

const AITaskAssignment: React.FC<AITaskAssignmentProps> = ({ careerPath, onChatWithMentor }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [submission, setSubmission] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    // Simular carga de tareas desde una API
    setTimeout(() => {
      setTasks(generateTasksForCareerPath(careerPath));
      setLoading(false);
    }, 1000);
  }, [careerPath]);

  const generateTasksForCareerPath = (path: string): Task[] => {
    const commonTasks: Task[] = [
      {
        id: '1',
        title: 'Análisis de mercado',
        description: 'Realiza un análisis de mercado para identificar oportunidades y amenazas en tu sector.',
        careerPath: 'common',
        difficulty: 'intermediate',
        deadline: '7 días',
        status: 'pending'
      },
      {
        id: '2',
        title: 'Plan de acción semanal',
        description: 'Desarrolla un plan de acción detallado para la próxima semana con objetivos SMART.',
        careerPath: 'common',
        difficulty: 'beginner',
        deadline: '2 días',
        status: 'pending'
      }
    ];

    const specificTasks: Record<string, Task[]> = {
      'product-manager': [
        {
          id: 'pm-1',
          title: 'Definición de producto fintech',
          description: 'Tu empresa quiere construir una nueva aplicación fintech. Define el público objetivo y crea un roadmap de producto.',
          careerPath: 'product-manager',
          difficulty: 'intermediate',
          deadline: '5 días',
          status: 'pending'
        },
        {
          id: 'pm-2',
          title: 'Priorización de funcionalidades',
          description: 'Utiliza la metodología RICE para priorizar las funcionalidades del próximo sprint.',
          careerPath: 'product-manager',
          difficulty: 'advanced',
          deadline: '3 días',
          status: 'pending'
        }
      ],
      'developer': [
        {
          id: 'dev-1',
          title: 'API de autenticación',
          description: 'Escribe una API backend para un sistema de autenticación y pruébala.',
          careerPath: 'developer',
          difficulty: 'intermediate',
          deadline: '7 días',
          status: 'pending'
        },
        {
          id: 'dev-2',
          title: 'Optimización de rendimiento',
          description: 'Identifica y soluciona problemas de rendimiento en el código proporcionado.',
          careerPath: 'developer',
          difficulty: 'advanced',
          deadline: '4 días',
          status: 'pending'
        }
      ],
      'entrepreneur': [
        {
          id: 'ent-1',
          title: 'Identificación de productos rentables',
          description: 'Encuentra tres productos rentables y lista posibles proveedores.',
          careerPath: 'entrepreneur',
          difficulty: 'intermediate',
          deadline: '5 días',
          status: 'pending'
        },
        {
          id: 'ent-2',
          title: 'Validación de idea de negocio',
          description: 'Diseña un experimento para validar tu idea de negocio con el mínimo costo posible.',
          careerPath: 'entrepreneur',
          difficulty: 'intermediate',
          deadline: '7 días',
          status: 'pending'
        }
      ],
      'tech-entrepreneur': [
        {
          id: 'tech-1',
          title: 'Identificación de brecha de mercado',
          description: 'Identifica una brecha en el mercado y elabora un modelo de negocio canvas para tu nuevo emprendimiento.',
          careerPath: 'tech-entrepreneur',
          difficulty: 'advanced',
          deadline: '7 días',
          status: 'pending'
        },
        {
          id: 'tech-2',
          title: 'Estrategia de adquisición de usuarios',
          description: 'Desarrolla una estrategia de adquisición de usuarios para tu startup con un presupuesto limitado.',
          careerPath: 'tech-entrepreneur',
          difficulty: 'intermediate',
          deadline: '5 días',
          status: 'pending'
        }
      ],
      'azure-admin': [
        {
          id: 'azure-1',
          title: 'Configuración de red virtual',
          description: 'Diseña e implementa una arquitectura de red virtual segura para un nuevo proyecto.',
          careerPath: 'azure-admin',
          difficulty: 'intermediate',
          deadline: '3 días',
          status: 'pending'
        },
        {
          id: 'azure-2',
          title: 'Optimización de costos',
          description: 'Analiza y optimiza los costos de recursos de Azure actuales.',
          careerPath: 'azure-admin',
          difficulty: 'advanced',
          deadline: '5 días',
          status: 'pending'
        }
      ]
    };

    return [...commonTasks, ...(specificTasks[path] || [])];
  };

  const handleTaskSelect = (task: Task) => {
    setSelectedTask(task);
    setSubmission('');
    setFeedback(null);
  };

  const handleSubmit = () => {
    if (!submission.trim() || !selectedTask) return;
    
    setSubmitting(true);
    
    // Simular envío y respuesta de la IA
    setTimeout(() => {
      setSubmitting(false);
      
      // Generar feedback basado en la carrera y la tarea
      const feedbackResponses: Record<string, string[]> = {
        'product-manager': [
          "✅ Fortalezas: Excelente trabajo identificando los puntos de dolor del cliente y las oportunidades de mercado.\n\n🔄 Mejoras: Considera añadir un análisis de competencia para un mejor posicionamiento. También sería útil incluir métricas específicas para medir el éxito de tu propuesta.",
          "✅ Fortalezas: Tu roadmap está bien estructurado y tiene una visión clara a largo plazo.\n\n🔄 Mejoras: Intenta priorizar mejor las funcionalidades basándote en el impacto para el usuario y el esfuerzo de desarrollo. Considera usar la metodología RICE o MoSCoW."
        ],
        'developer': [
          "✅ Fortalezas: Tu API maneja bien la autenticación y tiene una buena estructura.\n\n🔄 Mejoras: Optimiza la seguridad implementando tokens JWT y considera añadir rate limiting para prevenir ataques de fuerza bruta.",
          "✅ Fortalezas: Has identificado correctamente los cuellos de botella en el rendimiento.\n\n🔄 Mejoras: Considera implementar técnicas de caché y optimizar las consultas a la base de datos para mejorar aún más el rendimiento."
        ],
        'entrepreneur': [
          "✅ Fortalezas: Has seleccionado productos con tendencia creciente y buen margen de beneficio.\n\n🔄 Mejoras: Compara precios de proveedores antes de tomar una decisión y considera los costos de logística en tu análisis de rentabilidad.",
          "✅ Fortalezas: Tu experimento de validación es económico y bien enfocado.\n\n🔄 Mejoras: Asegúrate de definir claramente qué métricas considerarás como validación exitosa y ten un plan B si los resultados no son los esperados."
        ],
        'tech-entrepreneur': [
          "✅ Fortalezas: Tu modelo de negocio es innovador y resuelve un problema claro.\n\n🔄 Mejoras: Considera probar la demanda de los clientes antes de lanzar y define mejor tu propuesta de valor única frente a posibles competidores.",
          "✅ Fortalezas: Tu estrategia de adquisición es creativa y aprovecha bien los canales gratuitos.\n\n🔄 Mejoras: Establece KPIs claros para cada canal y prepara un plan de escalado una vez que identifiques los canales más efectivos."
        ],
        'azure-admin': [
          "✅ Fortalezas: Tu diseño de red virtual muestra una buena comprensión de la seguridad y la escalabilidad.\n\n🔄 Mejoras: Considera implementar redundancia geográfica y documentar mejor las políticas de acceso.",
          "✅ Fortalezas: Has identificado áreas clave para la optimización de costos.\n\n🔄 Mejoras: Incluye un análisis más detallado del ROI para cada recomendación y considera automatizar el escalado de recursos."
        ]
      };
      
      const defaultFeedback = "✅ Fortalezas: Has abordado bien los puntos principales de la tarea.\n\n🔄 Mejoras: Intenta profundizar más en el análisis y proporcionar ejemplos concretos para respaldar tus ideas.";
      
      const feedbackOptions = feedbackResponses[careerPath] || [defaultFeedback];
      const randomFeedback = feedbackOptions[Math.floor(Math.random() * feedbackOptions.length)];
      
      setFeedback(randomFeedback);
      
      // Actualizar el estado de la tarea a "submitted"
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === selectedTask.id 
            ? { ...task, status: 'submitted' as const } 
            : task
        )
      );
    }, 2000);
  };

  const getDifficultyBadge = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Principiante</span>;
      case 'intermediate':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Intermedio</span>;
      case 'advanced':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Avanzado</span>;
      default:
        return null;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <Clock className="mr-1 h-3 w-3" />
            Pendiente
          </span>
        );
      case 'in-progress':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            <Clock className="mr-1 h-3 w-3" />
            En progreso
          </span>
        );
      case 'submitted':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
            <CheckCircle className="mr-1 h-3 w-3" />
            Enviado
          </span>
        );
      case 'completed':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="mr-1 h-3 w-3" />
            Completado
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="animate-pulse flex space-x-4">
          <div className="flex-1 space-y-4 py-1">
            <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-lg overflow-hidden">
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
        <h2 className="text-lg font-medium flex items-center">
          <Lightbulb className="h-5 w-5 mr-2" />
          Sistema de Asignación de Tareas IA
        </h2>
        <p className="mt-1 text-sm text-white/80">
          Completa tareas del mundo real para desarrollar tus habilidades profesionales
        </p>
      </div>

      <div className="p-4 md:p-6">
        {!selectedTask ? (
          <>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Tareas disponibles</h3>
            <div className="space-y-4">
              {tasks.map((task) => (
                <div 
                  key={task.id}
                  className="border rounded-lg p-4 hover:border-accent hover:shadow-sm transition-all cursor-pointer"
                  onClick={() => handleTaskSelect(task)}
                >
                  <div className="flex justify-between items-start">
                    <h4 className="text-md font-medium text-gray-900">{task.title}</h4>
                    {getStatusBadge(task.status)}
                  </div>
                  <p className="mt-2 text-sm text-gray-600">{task.description}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      {getDifficultyBadge(task.difficulty)}
                      <span className="text-xs text-gray-500">Plazo: {task.deadline}</span>
                    </div>
                    <button
                      className="text-accent hover:text-accent/90 text-sm font-medium"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskSelect(task);
                      }}
                    >
                      Ver detalles
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 flex justify-between items-center">
              <button
                onClick={() => setSelectedTask(null)}
                className="text-accent hover:text-accent/90 text-sm font-medium"
              >
                ← Volver a la lista de tareas
              </button>
              <button
                onClick={onChatWithMentor}
                className="inline-flex items-center text-accent hover:text-accent/90 text-sm"
              >
                <MessageSquare className="h-4 w-4 mr-1" />
                Consultar mentor
              </button>
            </div>

            <div className="border rounded-lg p-4 mb-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">{selectedTask.title}</h3>
                {getStatusBadge(selectedTask.status)}
              </div>
              <div className="mt-2 flex items-center space-x-2">
                {getDifficultyBadge(selectedTask.difficulty)}
                <span className="text-xs text-gray-500">Plazo: {selectedTask.deadline}</span>
              </div>
              <p className="mt-4 text-gray-600">{selectedTask.description}</p>
            </div>

            {selectedTask.status !== 'submitted' && selectedTask.status !== 'completed' ? (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-2">Tu respuesta</h4>
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent"
                  rows={8}
                  placeholder="Escribe tu respuesta aquí..."
                  value={submission}
                  onChange={(e) => setSubmission(e.target.value)}
                ></textarea>
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleSubmit}
                    disabled={!submission.trim() || submitting}
                    className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white ${
                      !submission.trim() || submitting
                        ? 'bg-gray-300 cursor-not-allowed'
                        : 'bg-accent hover:bg-accent/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent'
                    }`}
                  >
                    {submitting ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Enviar respuesta
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <h4 className="text-md font-medium text-gray-900 mb-2">Feedback de la IA</h4>
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertTriangle className="h-5 w-5 text-blue-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-blue-800">Análisis de tu respuesta</h3>
                      <div className="mt-2 text-sm text-blue-700 whitespace-pre-line">
                        {feedback || "Tu respuesta está siendo analizada por nuestro sistema de IA. Recibirás feedback detallado pronto."}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AITaskAssignment;